import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AlertTriangle, Crown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

interface CustomMetricsTrialWarningProps {
  userId: string;
}

export const CustomMetricsTrialWarning = ({
 userId }: CustomMetricsTrialWarningProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Check if warning should be shown
  const { data: shouldShow } = useQuery({
    queryKey: ["custom-metrics-trial-warning", userId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("should_show_custom_metrics_trial_warning", {
        p_user_id: userId,
      });
      if (error) throw error;
      return data as boolean;
    },
    refetchInterval: 60000, // Check every minute
  });

  // Get days remaining
  const { data: daysRemaining } = useQuery({
    queryKey: ["custom-metrics-trial-days", userId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_custom_metrics_trial_days_remaining", {
        p_user_id: userId,
      });
      if (error) throw error;
      return data as number;
    },
    enabled: shouldShow === true,
    refetchInterval: 60000,
  });

  if (!shouldShow) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-4 overflow-hidden"
      >
        <div className="bg-gradient-to-r from-red-500/10 via-orange-500/10 to-red-500/10 border-2 border-red-500/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-red-600 dark:text-red-400">{t('ui.text.custom_metrics_trial_ending_soon')}</h3>
                {daysRemaining !== undefined && (
                  <span className="text-[10px] font-bold bg-red-500/20 text-red-600 dark:text-red-400 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {t(daysRemaining === 1 ? 'ui.text.day_left' : 'ui.text.days_left', { count: daysRemaining })}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t('ui.text.custom_metrics_trial_warning')}
              </p>
              <div className="flex gap-2 pt-1">
                <Button
                  onClick={() => navigate("/upgrade")}
                  size="sm"
                  className="h-8 text-xs font-semibold bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                >
                  <Crown className="w-3 h-3 mr-1.5" />{t('ui.text.upgrade_to_premium')}</Button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
